import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpClient, HttpBackend, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../service/core/auth.service';
import { StorageService } from '../service/core/storage.service';
import { Global } from '../service/core/Global';
import { Observable, catchError, finalize, map, of, shareReplay, switchMap, throwError } from 'rxjs';

const isAuthEndpoint = (url: string): boolean =>
    url.includes('decrypt-token') || url.includes('check-session') || url.includes('refresh-session') || url.includes('logout-session');

let refreshInflight: Observable<boolean> | null = null;

function refreshAccess(http: HttpClient): Observable<boolean> {
    if (!refreshInflight) {
        refreshInflight = http.post(Global.url_auth + 'refresh-session', {}, { withCredentials: true }).pipe(
            map(() => true),
            catchError(() => of(false)),
            finalize(() => { refreshInflight = null; }),
            shareReplay(1)
        );
    }
    return refreshInflight;
}

const handleAuthError = (
    err: HttpErrorResponse,
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
    cloned: HttpRequest<unknown>,
    auth: AuthService,
    logged: boolean,
    http: HttpClient
): Observable<HttpEvent<unknown>> => {
    const canRefresh = err.status === 401 && logged && !isAuthEndpoint(req.url);
    if (!canRefresh) {
        return throwError(() => err);
    }
    return refreshAccess(http).pipe(
        switchMap((ok) => {
            if (ok) return next(cloned);
            auth.sessionExpired$.next(true);
            return throwError(() => err);
        })
    );
};

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(AuthService);
    const storageService = inject(StorageService);
    const rawHttp = new HttpClient(inject(HttpBackend));
    if (req.url.includes('api-auth.adventistas.cl')) return next(req);
    const isLoggedIn = Boolean(storageService.getJsonValue('logged') ?? false);
    const isPublicFile = req.url.includes('/documents/view/');
    const clonedReq = req.clone({ withCredentials: !isPublicFile });
    return next(clonedReq).pipe(
        catchError((e: HttpErrorResponse) => handleAuthError(e, req, next, clonedReq, authService, isLoggedIn, rawHttp))
    );
};
